import * as React from "react";
import { useContext } from "react";
import { Route, RouteProps, Redirect, useLocation } from "react-router";
import { RouteType } from "@socialcoach/services";
import { SessionContext } from "@/providers";
import { ALL_USER_ROLES, SocialCoachSession } from "@/services/bedrock";
import {
  buildSignInUrl,
  clearStashedReturnTo,
  getReturnToFromSearch,
  peekStashedReturnTo,
  userDefaultRedirectUrl,
} from "@/utils";
import { Spinner } from "@/components/ui/spinner";

interface Props extends RouteProps {
  routeType?: RouteType;
  loaderComponent?: React.ReactNode;
  reservedGroups?: string[];
}

export const SCSessionRoute: React.FC<Props> = ({
  routeType = RouteType.PRIVATE_ROUTE,
  reservedGroups = ALL_USER_ROLES,
  loaderComponent,
  component: Component,
  render,
  ...rest
}) => {
  const { session, loading } = useContext(SessionContext);
  const location = useLocation();

  let isDev = false;
  let showRouteChanges = false;
  try {
    const metaEnv = new Function("return import.meta.env")();
    isDev = !!metaEnv?.DEV;
    showRouteChanges = metaEnv?.VITE_SHOW_ROUTE_CHANGES === "true";
  } catch (e) {
    // Falls back to false in non-ESM environments like Jest
  }

  if (isDev && showRouteChanges) {
    console.log("SCSessionRoute Evaluated:", {
      loading,
      hasSession: !!session,
      path: rest.path,
      role: session?.userRole,
    });
  }

  let effectiveRoleForRouting = session?.userRole || "";
  if (effectiveRoleForRouting === "ASSISTANT_COACH") {
    effectiveRoleForRouting = "COACH";
  }

  const shouldRenderExpectedRoute =
    (routeType === RouteType.PRIVATE_ROUTE && !!session && reservedGroups.includes(effectiveRoleForRouting)) ||
    (routeType === RouteType.PUBLIC_ROUTE && !session);

  // A signed-in user on a public route is about to be redirected (possibly to the stashed SSO return path); clear it
  // so it only applies once
  const isLeavingPublicRoute = routeType === RouteType.PUBLIC_ROUTE && !loading && !!session;
  React.useEffect(() => {
    if (isLeavingPublicRoute) {
      clearStashedReturnTo();
    }
  }, [isLeavingPublicRoute]);

  const getRedirectTarget = () => {
    const from = (location.state as any)?.from;

    if (from && routeType === RouteType.PUBLIC_ROUTE && from.pathname !== location.pathname) {
      return from;
    }

    // Unauthenticated user hitting a private page: send them to sign in and remember where they were going
    if (routeType === RouteType.PRIVATE_ROUTE && !session) {
      const [pathname, search] = buildSignInUrl(`${location.pathname}${location.search}${location.hash}`).split("?");
      return {
        pathname,
        search: search ? `?${search}` : undefined,
        state: { from: location },
      };
    }

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("pkce") === "true") {
      searchParams.delete("pkce");
      return {
        pathname: "/pkce-auth",
        search: `?${searchParams.toString()}`,
        state: undefined,
      };
    }

    // Checked after PKCE so a stale or extra returnTo can't hijack an OAuth handoff
    if (routeType === RouteType.PUBLIC_ROUTE) {
      const returnTo = getReturnToFromSearch(location.search) || peekStashedReturnTo();
      if (returnTo && returnTo.split(/[?#]/)[0] !== location.pathname) {
        return returnTo;
      }
    }

    const url = userDefaultRedirectUrl(session);
    const [pathname, search] = url.split("?");

    // Safety check: Prevent infinite redirect loops if the redirect path is the same as the current path
    if (pathname === location.pathname) {
      return {
        pathname: "/notfound",
        search: undefined,
        state: undefined,
      };
    }

    return {
      pathname,
      search,
      state: { from: location },
    };
  };

  if (loading) {
    return (
      <Route
        {...rest}
        render={() =>
          loaderComponent || (
            <div className="flex h-screen w-screen items-center justify-center bg-slate-50/50">
              <Spinner className="h-10 w-10 text-primary" />
            </div>
          )
        }
      />
    );
  }

  return (
    <Route
      {...rest}
      render={props => {
        if (shouldRenderExpectedRoute) {
          if (Component) {
            return <Component {...props} />;
          }
          if (render) {
            return render(props);
          }
          return null;
        }

        return <Redirect exact to={getRedirectTarget()} />;
      }}
    />
  );
};
